import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login(userId, password);
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${themeClasses.primary} p-4`}>
      <div className={`w-full max-w-md ${themeClasses.card} border ${themeClasses.border} rounded-2xl p-8 shadow-2xl`}>
        {/* Logo / Header */}
        <div className="flex flex-col items-center mb-8">
          <div className={`w-16 h-16 rounded-full bg-military-600 flex items-center justify-center mb-4 shadow-lg`}>
            <Shield size={32} className="text-white" />
          </div>
          <h1 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>FracAdapt</h1>
          <p className={`text-sm ${themeClasses.textMuted} mt-1`}>Fleet Management & Terrain Intelligence</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
              Username
            </label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
              placeholder="Enter your username"
              autoComplete="username"
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 pr-12 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textMuted} hover:${themeClasses.textSecondary} transition-colors`}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !userId || !password}
            className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
              submitting || !userId || !password
                ? 'bg-military-600/50 cursor-not-allowed'
                : 'bg-military-600 hover:bg-military-700 active:scale-[0.98] shadow-lg hover:shadow-xl'
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Authenticating...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className={`mt-8 text-center text-xs ${themeClasses.textMuted}`}>
          <p>Authorized personnel only</p>
          <p className="mt-1">Contact your administrator for access</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
